/**
 * Chat 页面铃铛 Hook
 * 职责：管理铃铛的 UI 状态、红点显示、点击跳转等逻辑
 */

"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useSnapshot } from "valtio";
import { type NotificationBellHead } from "./localNotificationState";
import { awaitNotifyBellListResync } from "./awaitNotifyBellListResync";
import {
  acknowledgeChatBell,
  chatBellSharedState,
  resetChatBellState,
  setChatBellLoading,
  updateChatBellHead,
} from "./chatBellSharedState";

import { rootStore } from "@store";
import {
  useLoginModalProvider,
  useNotifyMessageInfinite,
  useShellRouter,
  type NotifyMessageRow,
} from "@hooks";

function pickBellWriteHead(
  rows: NotifyMessageRow[],
): NotificationBellHead | null {
  const row = rows[0];
  if (!row?.id || typeof row.created !== "number") {
    return null;
  }
  return { id: row.id, time: row.created };
}

interface UseChatNotificationBellResult {
  showDot: boolean;
  isLoading: boolean;
  onBellClick: () => void;
  /**
   * After chat sync when the bell dot is visible: persist the same head as the red-dot
   * logic (`rows[0]` with `id` + `created`) via `setLatestSeen`, then clear the dot.
   * No-op if `showDot` is false. Skips while `isFetching` / `notifyLoading` to avoid racing
   * the same notify list query used on Chat. No navigation.
   */
  acknowledgeSyncedListHead: () => void;
}

interface UseChatNotificationBellDotResult {
  showDot: boolean;
  isLoading: boolean;
  acknowledge: () => void;
  refetch: () => Promise<void>;
}

interface UseNotificationBellNavigationResult {
  onBellClick: () => void;
}

/**
 * Chat 页面铃铛 Hook
 * @param userId 当前登录用户 ID（未登录时为 undefined）
 * @returns { showDot, isLoading, onBellClick, acknowledgeSyncedListHead }
 */
export function useChatNotificationBellDot(
  userId?: string,
): UseChatNotificationBellDotResult {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const bellSnapshot = useSnapshot(chatBellSharedState);

  const { rows, refetch: refetchNotifyList } = useNotifyMessageInfinite({
    enabled: false,
    staleTime: 0,
  });

  const refetch = useCallback(async () => {
    if (!userId || !isLoggedIn) {
      resetChatBellState(userId);
      return;
    }
    setChatBellLoading(userId, true);
    try {
      await awaitNotifyBellListResync(queryClient);
      await refetchNotifyList();
    } finally {
      setChatBellLoading(userId, false);
    }
  }, [userId, isLoggedIn, queryClient, refetchNotifyList]);

  useEffect(() => {
    if (!userId || !isLoggedIn) {
      resetChatBellState(userId);
      return;
    }

    const head = pickBellWriteHead(rows);
    updateChatBellHead(userId, head);
  }, [userId, isLoggedIn, rows]);

  const acknowledge = useCallback(() => {
    if (!userId || !isLoggedIn) {
      return;
    }
    acknowledgeChatBell(userId);
  }, [userId, isLoggedIn]);

  return {
    showDot: bellSnapshot.showDot,
    isLoading: bellSnapshot.isLoading,
    acknowledge,
    refetch,
  };
}

export function useNotificationBellNavigation(
  userId?: string,
): UseNotificationBellNavigationResult {
  const router = useShellRouter();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { open: openLoginModal } = useLoginModalProvider();

  const onBellClick = useCallback(() => {
    if (!userId || !isLoggedIn) {
      openLoginModal?.();
      return;
    }

    router.push("/notification");
  }, [userId, isLoggedIn, openLoginModal, router]);

  return { onBellClick };
}

/**
 * 兼容层：保留历史 API，内部组合 dot + navigation。
 */
export function useChatNotificationBell(
  userId?: string,
): UseChatNotificationBellResult {
  const { showDot, isLoading, acknowledge } =
    useChatNotificationBellDot(userId);
  const { onBellClick } = useNotificationBellNavigation(userId);

  return {
    showDot,
    isLoading,
    onBellClick,
    acknowledgeSyncedListHead: acknowledge,
  };
}
